/**
 * Various mathematic functions
 */
export class MathHlp {
  /**
   * Compare two floats with given precision
   * @param {number} a
   * @param {number} b
   * @param {number} precision @default Number.EPSILON
   * @return {boolean}
   */
  public static isNear(a: number, b: number, precision = Number.EPSILON): boolean {
    return Math.abs(a - b) < precision;
  }

  /**
   * Checks if number fits in given range (inclusive)
   * @param {number} x
   * @param {number} from
   * @param {number} to
   * @return {boolean}
   */
  public static inRange(x: number, from: number, to: number): boolean {
    return from <= x && x <= to;
  }

  /**
   * Checks if number fits in given range (exclusive)
   * @param {number} x
   * @param {number} from
   * @param {number} to
   * @return {boolean}
   */
  public static inRangeEx(x: number, from: number, to: number): boolean {
    return from < x && x < to;
  }

  /**
   * Limits number to given range (inclusive)
   * @param {number} x
   * @param {number} min
   * @param {number} max
   * @return {number}
   */
  public static clamp(x: number, min: number, max: number): number {
    return Math.min(Math.max(x, min), max);
  }

  /**
   * Calculates rounded percent value.
   * @param {number} value
   * @param {number} total
   * @return {number}
   */
  public static percent(value: number, total: number): number {
    return total > 0 ? Math.round((value / total) * 100) : 0;
  }

  /**
   * Calculates rounded percent value with small non-zero precision.
   * @param {number} value
   * @param {number} total
   * @return {number}
   */
  public static precisePercent(value: number, total: number): number {
    if (total <= 0) return 0;

    const percent = (value / total) * 100;
    return percent > 0 && percent < 1 ? Math.round(percent * 10) / 10 : Math.round(percent);
  }

  /**
   * @param {number} x
   * @return {number} signed Math.floor() of absolute value
   */
  public static signFloor(x: number): number {
    return Math.sign(x) * Math.floor(Math.abs(x));
  }

  /**
   * @param {number} x
   * @return {number} signed Math.ceil() of absolute value
   */
  public static signCeil(x: number): number {
    return Math.sign(x) * Math.ceil(Math.abs(x));
  }

  /**
   * Divide two integers with reminder, returns both values
   * @param {number} x
   * @param {number} y must be integer
   * @return {[number, number]}
   */
  public static divRem(x: number, y: number): [number, number] {
    const reminder = x % y;
    const quotient = (x - reminder) / y;
    return [quotient, reminder];
  }

  /**
   * Asserts that a value is a positive safe integer.
   * @param {number} value Value to validate.
   * @param {string} name Field name used in an error.
   */
  public static assertPositiveSafeInteger(value: number, name: string): void {
    if (!Number.isSafeInteger(value) || value <= 0) {
      throw new Error(`${name} must be a positive safe integer`);
    }
  }
}

